// src/routes/waypoint/history.tsx
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Mic } from "lucide-react";
import { useState } from "react";
import { useWaypointSessionHistory } from "@/hooks/use-waypoint-sessions";

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const DIFFICULTY_LABELS = {
  foundation: "Foundation",
  standard: "Standard",
  stretch: "Stretch",
} as const;

const STATUS_LABELS = {
  briefing: "Ready",
  active: "In progress",
  ended: "Ended",
} as const;

export function WaypointHistoryPage() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState<string | null>(null);
  const [direction, setDirection] = useState<"next" | "prev" | undefined>(undefined);
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useWaypointSessionHistory({ cursor, direction });

  const sessions = data?.data ?? [];

  function goNext() {
    if (data?.nextCursor) {
      setCursor(data.nextCursor);
      setDirection("next");
      setPage((p) => p + 1);
    }
  }

  function goPrev() {
    if (data?.prevCursor) {
      setCursor(data.prevCursor);
      setDirection("prev");
      setPage((p) => p - 1);
    }
  }

  const total = data?.total ?? 0;
  const pageSize = 20;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div style={{ animation: "wp-fade 0.3s ease-out" }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            color: "#407EC1",
            marginBottom: 4,
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          History
        </p>
        <h1
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 800,
            fontSize: 28,
            color: "#001847",
            margin: 0,
          }}
        >
          All sessions
        </h1>
      </div>

      {/* Loading skeletons */}
      {isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              style={{
                height: 64,
                borderRadius: 10,
                background: "#f0f4fb",
                animation: "pulse 2s infinite",
              }}
            />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div
          style={{
            padding: "48px 0",
            textAlign: "center",
            color: "#94a3b8",
            fontSize: 14,
          }}
        >
          Failed to load sessions. Please try again.
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && sessions.length === 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "48px 0",
            color: "#94a3b8",
            gap: 12,
          }}
        >
          <Mic style={{ width: 40, height: 40, opacity: 0.3 }} />
          <p style={{ margin: 0, fontSize: 14 }}>No sessions yet. Start one from the dashboard.</p>
        </div>
      )}

      {/* Session list */}
      {!isLoading && !isError && sessions.length > 0 && (
        <div>
          {/* Header row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.6fr 1fr 0.9fr auto",
              gap: 12,
              padding: "0 16px 8px",
              borderBottom: "1px solid #e2e8f0",
              marginBottom: 8,
            }}
          >
            {["Scenario", "When", "Status", ""].map((h) => (
              <span
                key={h}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: "#94a3b8",
                }}
              >
                {h}
              </span>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {sessions.map((session) => {
              const isEnded = session.status === "ended";
              return (
                <button
                  type="button"
                  key={session.id}
                  onClick={() =>
                    navigate({
                      to: isEnded
                        ? "/dashboard/waypoint/$sessionId/debrief"
                        : "/dashboard/waypoint/$sessionId",
                      params: { sessionId: session.id },
                    })
                  }
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.6fr 1fr 0.9fr auto",
                    gap: 12,
                    padding: "14px 16px",
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                    alignItems: "center",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "#407EC1";
                    (e.currentTarget as HTMLButtonElement).style.boxShadow =
                      "0 2px 8px rgba(30,108,199,0.10)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0";
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                        fontSize: 13,
                        color: "#001847",
                        margin: "0 0 2px",
                      }}
                    >
                      {session.scenarioName ?? "Session"}
                    </p>
                    <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>
                      {DIFFICULTY_LABELS[session.difficulty]}
                    </p>
                  </div>
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    {timeAgo(session.createdAt)}
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "3px 8px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      background:
                        session.status === "ended"
                          ? "#E4F4EC"
                          : session.status === "active"
                            ? "#FEF3C7"
                            : "#E8F2FB",
                      color:
                        session.status === "ended"
                          ? "#2EA86A"
                          : session.status === "active"
                            ? "#E8A93C"
                            : "#407EC1",
                    }}
                  >
                    {STATUS_LABELS[session.status]}
                  </span>
                  <ArrowRight style={{ width: 14, height: 14, color: "#94a3b8" }} />
                </button>
              );
            })}
          </div>

          {/* Pagination footer */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 20,
              paddingTop: 16,
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <span style={{ fontSize: 12, color: "#64748b" }}>
              {total > 0 ? `${from}–${to} of ${total}` : ""}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                disabled={!data?.hasPrev}
                onClick={goPrev}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 14px",
                  background: data?.hasPrev ? "#fff" : "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  cursor: data?.hasPrev ? "pointer" : "default",
                  color: data?.hasPrev ? "#374151" : "#94a3b8",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <ArrowLeft style={{ width: 13, height: 13 }} />
                Prev
              </button>
              <button
                type="button"
                disabled={!data?.hasNext}
                onClick={goNext}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 14px",
                  background: data?.hasNext ? "#fff" : "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  cursor: data?.hasNext ? "pointer" : "default",
                  color: data?.hasNext ? "#374151" : "#94a3b8",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Next
                <ArrowRight style={{ width: 13, height: 13 }} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
